import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Buildings, Calendar, Globe, BookOpenText, TrendUp } from "@phosphor-icons/react";

export default function PublisherDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pub, setPub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/publishers/${id}`);
        setPub(data);
      } catch (e) {
        toast.error("Editora não encontrada");
        navigate("/editoras");
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  if (loading) return <div className="pt-32 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>;
  if (!pub) return null;

  return (
    <div className="pt-20 max-w-6xl mx-auto px-5 lg:px-8 pb-24" data-testid="publisher-detail-page">
      <Link to="/editoras" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6">
        <ArrowLeft size={16} weight="bold" /> Voltar às editoras
      </Link>

      <div className="bg-[#121217] border border-white/5 rounded-2xl p-8 flex flex-col md:flex-row gap-8">
        {pub.image_url ? (
          <img src={pub.image_url} alt={pub.name} className="w-40 h-40 object-contain rounded-xl bg-white/5 p-3 flex-shrink-0" />
        ) : (
          <div className="w-40 h-40 rounded-xl bg-gradient-to-br from-[#E63946]/20 to-[#7209B7]/20 grid place-items-center flex-shrink-0">
            <Buildings size={56} weight="duotone" className="text-[#E63946]" />
          </div>
        )}
        <div className="flex-1">
          <div className="font-mono-accent text-xs text-[#E63946] mb-2">◢ EDITORA</div>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">{pub.name}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {pub.founded_year && (
              <span className="flex items-center gap-2 text-zinc-300"><Calendar size={14} weight="bold" className="text-[#7209B7]" /> Fundada em <strong className="text-white">{pub.founded_year}</strong></span>
            )}
            <span className="flex items-center gap-2 text-zinc-300"><BookOpenText size={14} weight="bold" className="text-[#E63946]" /> <strong className="text-white">{pub.manga_count}</strong> mangás</span>
            {pub.website && (
              <a href={pub.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#E63946] hover:underline" data-testid="publisher-website-link">
                <Globe size={14} weight="bold" /> Website
              </a>
            )}
          </div>
          {pub.description && <p className="mt-5 text-zinc-300 whitespace-pre-line leading-relaxed">{pub.description}</p>}
        </div>
      </div>

      {pub.recent_manga.length > 0 && (
        <section className="mt-12">
          <div className="font-mono-accent text-xs text-[#E63946] mb-3">◢ RECENTES</div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mb-6">Recentes desta editora</h2>
          <MangaRow items={pub.recent_manga} testid="publisher-recent" />
        </section>
      )}

      {pub.popular_manga.some(m => (m.view_count || 0) > 0) && (
        <section className="mt-12">
          <div className="font-mono-accent text-xs text-[#F59E0B] mb-3 flex items-center gap-2"><TrendUp size={14} weight="bold" /> ◢ MAIS VISTOS</div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mb-6">Mais vistos desta editora</h2>
          <MangaRow items={pub.popular_manga.filter(m => (m.view_count || 0) > 0)} testid="publisher-popular" showViews />
        </section>
      )}

      {pub.manga_count > 0 && (
        <div className="mt-10 text-center">
          <Link to={`/database?q=${encodeURIComponent(pub.name)}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-white/15 hover:bg-white/5 transition-colors text-sm">
            Ver todos os mangás da {pub.name}
          </Link>
        </div>
      )}
    </div>
  );
}

function MangaRow({ items, testid, showViews }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
      {items.map((m) => (
        <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="group" data-testid={`${testid}-${m.manga_id}`}>
          <div className="aspect-[2/3] overflow-hidden rounded-xl bg-[#121217] border border-white/5 relative">
            {m.cover_url ? (
              <img src={m.cover_url} alt={m.title} className="manga-cover w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-zinc-700 font-mono-accent text-xs">SEM CAPA</div>
            )}
            {showViews && (m.view_count || 0) > 0 && (
              <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 text-[10px] font-mono-accent flex items-center gap-1">
                <TrendUp size={10} weight="bold" className="text-[#E63946]" /> {m.view_count}
              </div>
            )}
          </div>
          <div className="mt-3 font-display font-semibold text-sm leading-tight line-clamp-2 group-hover:text-[#E63946] transition-colors">{m.title}</div>
          {m.author && <div className="font-mono-accent text-[10px] text-zinc-500 mt-1">{m.author}</div>}
        </Link>
      ))}
    </div>
  );
}
